import { axiosClient } from "../api/axiosClient";
import type {
  Permission,
  PermissionCategory,
  MyPermissionsDetailed,
} from "gsintegrations";

/**
 * Permission Repository
 *
 * Handles all permission-related API calls
 */
export const permissionRepository = {
  /**
   * Get all permissions for a terreiro context
   */
  async getAll(terreiroId: string): Promise<Permission[]> {
    const response = await axiosClient.get<Permission[]>(
      `/terreiros/${terreiroId}/permissions`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Get permissions by category for a terreiro context
   */
  async getByCategory(
    terreiroId: string,
    category: PermissionCategory,
  ): Promise<Permission[]> {
    const response = await axiosClient.get<Permission[]>(
      `/terreiros/${terreiroId}/permissions/category/${category}`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Get current user's permissions for a terreiro
   */
  async getMyPermissions(terreiroId: string): Promise<string[]> {
    const response = await axiosClient.get<{ permissions: string[] }>(
      `/terreiros/${terreiroId}/my-permissions`,
      {
        withCredentials: true,
      },
    );
    return response.data?.permissions ?? [];
  },

  /**
   * Get detailed permissions for a user in a terreiro
   * Includes role information, full permission details, and categorized permissions
   */
  async getMyPermissionsDetailed(terreiroId: string): Promise<MyPermissionsDetailed> {
    const response = await axiosClient.get<MyPermissionsDetailed>(
      `/terreiros/${terreiroId}/my-permissions/details`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Get current user's permissions for personal profile scope.
   */
  async getMyPersonalPermissions(): Promise<string[]> {
    const response = await axiosClient.get<{ permissions: string[] }>(
      "/my-permissions/personal",
      {
        withCredentials: true,
      },
    );

    return response.data?.permissions ?? [];
  },

  /**
   * Get detailed permissions for personal profile scope.
   */
  async getMyPersonalPermissionsDetailed(): Promise<MyPermissionsDetailed> {
    const response = await axiosClient.get<MyPermissionsDetailed>(
      "/my-permissions/personal/details",
      {
        withCredentials: true,
      },
    );

    return response.data;
  },
};
